/**
 * @author Trendrating <info@trendrating.net>
 *
 * @module api/account/Users
 * @summary Requests for users
 *
 */

import { _Base } from "../_Base";
import { endpoints } from "../endpoints";
import { decodeUser } from "./Decoder";

export class Users extends _Base {
  /**
   * Send email to support
   *
   * @param {object} params - request parameters
   * @param {string} params.from - email address of the user
   * @param {string} params.fromName - Name and surname of the user
   * @param {string} params.message - email message
   *
   * @returns {Promise} a promise fulfilled with the
   *       handled data of the response
   */
  // askSupport(params: any): any {
  //     const endPointRoot = this.getEndpointRoot(this.environment.api.rest);
  //     const url = endPointRoot + endpoints.accounts.users.askSupport;

  //     const paramsAskSupport = deepClone(params);
  //     paramsAskSupport.additionalCookies =
  //         this.environment.account.user?.additionalCookies;

  //     return this.preparePost(url, paramsAskSupport);
  // }

  /**
   * Get users
   *
   * @param {object} params - request parameters
   * @param {Array}  params.ids - user ids to be retrieved
   * @param {Array}  params.properties - properties to be retrieved
   *
   * @returns {Promise} a promise fulfilled with the
   *       handled data of the response
   */
  get(params: any) {
    const endPointRoot = process.env.REACT_APP_ROOT_T_REST;
    const url = endPointRoot + endpoints.accounts.users.get;

    const paramsGet: any = [
      {
        classType: "USER",
        extendedRequest: {
          onDemandResult: [],
        },
        id: params["ids"] != null ? params["ids"] : null,
      },
    ];
    for (const property of params["properties"]) {
      paramsGet[0]["extendedRequest"]["onDemandResult"].push({
        dimension: property,
      });
    }

    return this.preparePost(url, paramsGet, null);
  }

  /**
   * Get users by filters (new API)
   *
   * @param {object} params - request parameters
   * @param {Array}  params.ids - (optional) user ids to be retrieved
   * @param {Array}  params.active - search by active status
   *
   * @returns {dojo/promise/Promise} a promise fulfilled with the
   *       handled data of the response
   */

  async select(payload: {
    searches: { filters: { dimension: string; segments: any[] }[] }[];
    sort: { dimension: string; rev: boolean };
  }) {
    const endPointRoot = process.env.REACT_APP_ROOT_T_REST;
    const url = endPointRoot + endpoints.accounts.users.getByIdsFilter;

    const response = await this.preparePost(url, payload);

    return response?.data?.ids;
  }

  async getByFilter(params: any) {
    const endPointRoot = process.env.REACT_APP_ROOT_T_REST;
    const url = endPointRoot + endpoints.accounts.users.getByIdsFilter;

    const _params: any = {
      searches: [
        {
          filters: [],
        },
      ],
    };

    if (params != null) {
      if (params.ids != null) {
        _params.searches[0].filters.push({
          dimension: "ownerId",
          segments: params.ids,
        });
      }
      if (params.active != null) {
        _params.searches[0].filters.push({
          dimension: "active",
          segments: [params.active],
        });
      }
    }

    const response = await this.preparePost(url, _params, null);
    return response.data.ids;
  }

  /**
   * Impersonate another user
   *
   * @param {number} userId - the ID of the user to impersonate
   *
   * @returns {Promise} a promise fulfilled with the
   *       handled data of the response
   */
  async impersonate(userId: any) {
    const endPointRoot = process.env.REACT_APP_ROOT_T_REST;
    const url = endPointRoot + endpoints.accounts.users.impersonate;

    const response = await this.prepareGet(url, { id: userId }, null);
    return response.data.user;
  }

  async getUserPermissions(userID: number, headers) {
    const endPointRoot = process.env.REACT_APP_ROOT_T_REST;
    const url = endPointRoot + endpoints.accounts.users.getUserPermissions;
    const params = { userId: userID };
    const response = await this.prepareGet(url, params, headers);
    return response;
  }

  /**
   * User login
   *
   * @param {object} params - login parameters
   * @param {string} params.email - user email
   * @param {string} params.password - user password
   *
   * @returns {Promise} a promise fulfilled with the
   *       handled data of the response
   */
  async login({ email, password }: any) {
    const endPointRoot = process.env.REACT_APP_ROOT_T_REST;
    const urlCookies = endPointRoot + endpoints.accounts.sessions.remove;
    const urlLogin = endPointRoot + endpoints.accounts.users.login;

    const basicAuth = this._basicAuth(email, password);

    const headers = {
      Authorization: basicAuth,
    };

    /*const removeCookiesResponse =*/ await this.prepareGet(
      urlCookies,
      null,
      null
    );

    const response = await this.preparePost(
      urlLogin,
      { email, password },
      headers
    );

    //!--
    const _url = endPointRoot + endpoints.accounts.sessions.get;
    const _response = await this.prepareGet(_url, {
      testget: "check",
    });
    //!--

    const JSESSIONID = _response?.data?.JSESSIONID;
    response.data.JSESSIONID = JSESSIONID;

    const decodedUser = decodeUser(response);
    // //get user permissions
    // let permissions = await this.getUserPermissions(
    //   parseInt(decodedUser.id),
    //   basicAuth
    // );
    // if (permissions) {
    //   decodedUser["permissions"] = permissions.data.permissions;
    // }
    return decodedUser;
  }

  /**
   * User logout
   *
   * @returns {Promise} a promise fulfilled with the
   *       handled data of the response
   */
  logout() {
    const endPointRoot = process.env.REACT_APP_ROOT_T_REST;
    const url = endPointRoot + endpoints.accounts.users.logout;
    return this.prepareGet(url, null, null);
  }
  _basicAuth(email: string, password: string) {
    const token = email + ":" + password;
    const textEncoder = new TextEncoder();
    const hash = window.btoa(
      String.fromCharCode(...new Uint8Array(textEncoder.encode(token)))
    );

    return "Basic " + hash;
  }

  deleteUser(userID, dryRun, erase) {
    const endPointRoot = process.env.REACT_APP_ROOT_T_REST;
    const url = endPointRoot + endpoints.accounts.users.delete;
    return this.prepareGet(
      url,
      { id: userID, dryRun: dryRun, erase: erase },
      null
    );
  }

  async getUserOnline(_filters) {
    const endPointRoot = process.env.REACT_APP_ROOT_T_REST;
    const url = endPointRoot + endpoints.accounts.users.onlineSearch;

    const params = {
      filters: _filters,
      sort: { dimension: "id", rev: false },
    };
    return this.preparePost(url, params, null);
  }

  async searchPendingUsersOnline() {
    const endPointRoot = process.env.REACT_APP_ROOT_T_REST;
    const url =
      endPointRoot + endpoints.accounts.users.pendingOnlineUsersSearch;

    const params = {
      sort: {
        dimension: "id",
        rev: false,
      },
    };

    return this.preparePost(url, params);
  }
}
